/**
 * Standalone IIFE entry point for @nowramp/form.
 *
 * Usage (PHP / plain HTML):
 *   <script src="https://cdn.example.com/form/latest/nowramp-form.iife.js"></script>
 *   <div id="nowramp"></div>
 *   <script>
 *     NowRampForm.mount('#nowramp', {
 *       projectId: 'proj_xxx',
 *       apiUrl: 'https://api.nowramp.com',
 *       assetBaseUrl: 'https://cdn.example.com/form/latest',
 *     });
 *   </script>
 */

import './styles/index.css';
import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { RampForm, type RampFormProps } from './components/RampForm';

const roots = new Map<Element, Root>();

/**
 * Mount RampForm into a DOM element.
 * @param selector CSS selector or DOM element
 * @param props    RampFormProps (projectId, apiUrl, theme, etc.)
 */
function mount(
  selector: string | Element,
  props: Omit<RampFormProps, 'children'>,
): void {
  const el = typeof selector === 'string'
    ? document.querySelector(selector)
    : selector;

  if (!el) {
    console.error(`[NowRampForm] Element not found: ${selector}`);
    return;
  }

  // Unmount existing root on the same element
  const existing = roots.get(el);
  if (existing) {
    existing.unmount();
    roots.delete(el);
  }

  const root = createRoot(el);
  roots.set(el, root);
  root.render(React.createElement(RampForm, props));
}

/**
 * Unmount a previously mounted RampForm.
 * @param selector CSS selector or DOM element used in mount()
 */
function unmount(selector: string | Element): void {
  const el = typeof selector === 'string'
    ? document.querySelector(selector)
    : selector;

  if (!el) return;

  const root = roots.get(el);
  if (root) {
    root.unmount();
    roots.delete(el);
  }
}

export { mount, unmount, RampForm };
export type { RampFormProps };
